import { zodResolver } from '@hookform/resolvers/zod';import {
  Button,
  capitalize,
  Checkbox,
  MenuItem,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { ProfileRoleType } from '@strength/db-schema';
import {
  BlockType,
  CRICKET_BOWLING_COACH_PROFILE_ROLE,
  zBlockType,
} from '@strength/domain';
import { areBlockWorkoutsRemoved } from '@strength/domain-workout';
import { I18nTranslator } from '@strength/i18n';
import { MdInfo } from '@teamworksdev/icons';
import { ChangeEvent, useCallback, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { DateTime } from 'luxon';
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
} from '../../../../components/drawer';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '../../../../components/form/form';
import { FormNotification } from '../../../../components/form/form-notification';
import { MultiSelect } from '../../../../components/input/multi-select';
import { StringDatePicker } from '../../../../components/input/string-date-picker';
import { ConfirmationModal } from '../../../../components/modals/confirmation-modal';
import {
  deleteBlockText,
  deleteBlockWorkoutsText,
  DeleteConfirmationModal,
} from '../../../../components/modals/delete-confirmation-modal';
import { InfoTooltip } from '../../../../components/tooltip/info-tooltip';
import { cn } from '../../../../components/utils';
import { useFlags } from '../../../../context/featureFlag';
import { useViewer } from '../../../../context/viewer';
import { getLocale } from '../../../../utils/i18n';
import { Block, TrackPreview } from '../types';
import {
  blockStartDateLockReason,
  startDateShiftErrorKey,
  type BlockStartDateLockReason,
} from '../annual-planner-access';
import { getBlockEndDateFromForm } from '../utils';
import {
  blockForm,
  CRICKET_BOWLING_BLOCK_SCHEDULE,
  getBlockScheduleFromCount,
  getDefaultCycleLengthForBlockType,
  getDefaultValues,
  type BlockForm,
} from './block-form-schema';
import { BlockLayoutInput } from './BlockLayoutInput';

export function BlockSheet({
  open,
  focusOptions,
  cycleLabel,
  sessionLabel,
  defaultValues,
  hasBeenPublished,
  onSave,
  onClose,
  state,
  tracks,
  onDelete,
}: {
  open: boolean;
  focusOptions: { name: string; id: string }[];
  cycleLabel: string | null;
  sessionLabel: string | null;
  defaultValues: Partial<Block>;
  hasBeenPublished: boolean;
  onSave: (form: BlockForm) => void;
  onClose: () => void;
  state: 'add' | 'edit' | 'copy';
  tracks: TrackPreview[];
  onDelete?: () => void;
}) {
  const {
    palette: { mediumEmphasisTextLight },
  } = useTheme();
  const { t } = useTranslation();
  const { bowlingBlockTypeFlag } = useFlags();

  const {
    profile: { role },
  } = useViewer();

  const blockTypeOptions = useMemo(() => {
    return getBlockTypeOptions(role);
  }, [role]);

  const isCopy = state === 'copy';
  const isEdit = state === 'edit';

  const actionTerm = isEdit
    ? t('editTrainingBlock')
    : isCopy
      ? t('pasteTrainingBlock')
      : t('createTrainingBlock');
  const defaultFormValues = getDefaultValues(
    defaultValues,
    isCopy,
    blockTypeOptions,
  );

  // A start-date change may never move the schedule into the past: a block
  // that starts in the past may only move forward (its new start date may
  // still be in the past); a block that starts today or later may not move
  // into the past. Mirrors the API rule (assertStartDateShiftTargetAllowed)
  // so the drawer catches it before the save. "Today" is the UTC calendar
  // date, the same value the API checks, so the two rules agree near UTC
  // midnight instead of the browser's local midnight.
  const today = DateTime.fromISO(DateTime.utc().toISODate()!);
  const currentStartDate = defaultValues.startDate
    ? DateTime.fromISO(defaultValues.startDate)
    : null;
  const startDateMin =
    isEdit && currentStartDate?.isValid
      ? currentStartDate < today
        ? currentStartDate
        : today
      : undefined;

  const editBlockForm = useMemo(
    () =>
      blockForm.superRefine((data, ctx) => {
        if (data.startDate === defaultValues.startDate) return;
        if (!defaultValues.startDate || !data.startDate) return;
        // "Today" is the UTC calendar date, the same value the API checks,
        // so the two rules agree near UTC midnight instead of the browser's
        // local midnight (see `today` above).
        const errorKey = startDateShiftErrorKey(
          defaultValues.startDate,
          data.startDate,
          today.toISODate()!,
        );
        if (errorKey) {
          ctx.addIssue({
            code: 'custom',
            path: ['startDate'],
            message: t(errorKey),
          });
        }
      }),
    // today.toISODate() collapses to one value per calendar day, so the
    // schema is stable within a mount and across midnight.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [defaultValues.startDate, isEdit, t, today.toISODate()],
  );

  const form = useForm({
    mode: 'onChange',
    resolver: zodResolver(isEdit ? editBlockForm : blockForm),
    defaultValues: defaultFormValues,
  });
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteWeeksModalOpen, setDeleteWeeksModalOpen] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
  } = form;

  const blockType = form.watch('blockType');
  const isBowlingEdit =
    isEdit && bowlingBlockTypeFlag && blockType === 'bowling';

  // Why the start date cannot move, null when it can. Blocks with logged
  // (in-progress) or completed workouts and bowling blocks refuse the change
  // (the API rejects it too); published blocks stay locked until the
  // automatic re-publish path lands (Phase 02).
  const startDateLock: BlockStartDateLockReason | null = isEdit
    ? blockStartDateLockReason(defaultValues)
    : null;

  // if the block type is changed from strength to bowling,
  // we need to reset the block layout
  const onTypeChange = (event: ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as string;
    // If block type is switched to 'bowling', reset the blockLayout field
    if (value === 'bowling' && form.watch('blockType') !== 'bowling') {
      resetBlockLayoutForBowling();
    }
    form.setValue('blockType', value as BlockType);
  };

  const resetBlockLayoutForBowling = () => {
    const defaultSchedule = CRICKET_BOWLING_BLOCK_SCHEDULE;
    form.setValue('blockLayout', defaultSchedule);
    form.setValue('cycleCount', defaultSchedule.length);
    form.setValue('sessionCount', getDefaultCycleLengthForBlockType(true));
  };

  const closeHandler = useCallback(() => {
    if (isDirty) {
      setCancelModalOpen(true);
    } else {
      onClose();
    }
  }, [isDirty, onClose]);

  return (
    <Drawer open={open} onClose={closeHandler}>
      <DrawerHeader onClose={closeHandler}>
        <Typography variant="h3">{actionTerm}</Typography>
        {isCopy && (
          <Typography variant="body2" color={mediumEmphasisTextLight}>
            {t('confirmBlockDetails')}
          </Typography>
        )}
      </DrawerHeader>

      <Form {...form}>
        <form
          id="block-form"
          onSubmit={handleSubmit((data) => {
            const workoutsRemoved =
              isEdit &&
              areBlockWorkoutsRemoved(
                defaultFormValues.blockLayout,
                data.blockLayout,
              );
            if (workoutsRemoved) {
              setDeleteWeeksModalOpen(true);
            } else {
              onSave(data);
            }
          })}
          className="gap-4 flex flex-col flex-grow overflow-y-auto"
        >
          <div className="flex flex-col px-8 py-4 gap-4">
            {isCopy && (
              <FormNotification
                title={t('newWorkoutDates')}
                type="info"
                className="mb-2 max-w-[400px]"
              >
                {t('workoutDatesMustBeReentered')}
              </FormNotification>
            )}
            <FormField
              name={'name'}
              control={control}
              render={({ field }) => (
                <FormItem>
                  <TextField
                    label={t('name')}
                    {...field}
                    error={Boolean(errors.name)}
                    helperText={errors.name?.message}
                    sx={{ width: '100%' }}
                  />
                </FormItem>
              )}
            />

            <FormField
              name={'trackId'}
              control={control}
              render={({ field }) => (
                <FormItem>
                  <TextField
                    label={t('track')}
                    select
                    onChange={field.onChange}
                    value={field.value}
                    fullWidth={true}
                    disabled={isEdit}
                  >
                    {tracks.map(({ id, name }) => (
                      <MenuItem key={id} value={id}>
                        {name}
                      </MenuItem>
                    ))}
                  </TextField>
                </FormItem>
              )}
            />

            {bowlingBlockTypeFlag && (
              <FormField
                name={'blockType'}
                control={control}
                render={({ field }) => (
                  <FormItem>
                    <TextField
                      label={t('type')}
                      select
                      onChange={onTypeChange}
                      value={field.value}
                      fullWidth={true}
                      disabled={isEdit}
                    >
                      {blockTypeOptions.map((type) => (
                        <MenuItem key={type} value={type}>
                          {getBlockTypeOptionLabel(type as BlockType, t)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </FormItem>
                )}
              />
            )}

            <FormField
              name={'startDate'}
              control={control}
              render={({
                field: { onChange, value, ref },
                fieldState: { error },
              }) => {
                // Current vs New preview: shown once a valid new start date
                // is entered. The new range keeps the block's duration and
                // matches what the save writes (getBlockEndDateFromForm is
                // the same helper the edit input uses).
                const newStart = value ? DateTime.fromISO(value) : null;
                const dateChanged =
                  isEdit &&
                  !!newStart?.isValid &&
                  value !== defaultValues.startDate;
                const newEndDate =
                  dateChanged && value
                    ? getBlockEndDateFromForm(value, form.watch('blockLayout'))
                    : null;

                const formatDate = (isoDate: string) =>
                  DateTime.fromISO(isoDate).toLocaleString(
                    DateTime.DATE_SHORT,
                    {
                      locale: getLocale(),
                    },
                  );

                return (
                  <FormItem>
                    <StringDatePicker
                      label={t('startDate')}
                      onChange={onChange}
                      value={value}
                      inputRef={ref}
                      error={Boolean(error)}
                      errorMessage={error?.message}
                      disabled={startDateLock !== null}
                      minDate={startDateMin}
                      sx={{ width: '100%' }}
                    />
                    {(startDateLock === 'inProgress' ||
                      startDateLock === 'bowling') && (
                      <FormNotification
                        title={t('startDateLocked')}
                        type="info"
                        className="mt-2 max-w-[400px]"
                      >
                        {startDateLock === 'inProgress'
                          ? t('startDateLockedInProgress')
                          : t('startDateLockedBowling')}
                      </FormNotification>
                    )}
                    {isEdit && (
                      <div
                        aria-live="polite"
                        className="mt-2 flex flex-col gap-1 max-w-[400px]"
                      >
                        {dateChanged &&
                          newEndDate &&
                          defaultValues.startDate &&
                          defaultValues.endDate && (
                            <>
                              <Typography
                                variant="body2"
                                color={mediumEmphasisTextLight}
                              >
                                {t('currentDateRange', {
                                  start: formatDate(defaultValues.startDate),
                                  end: formatDate(defaultValues.endDate),
                                })}
                              </Typography>
                              <Typography variant="body2">
                                {t('newDateRange', {
                                  start: formatDate(value),
                                  end: formatDate(newEndDate),
                                })}
                              </Typography>
                            </>
                          )}
                      </div>
                    )}
                  </FormItem>
                );
              }}
            />
            {isEdit || isCopy ? (
              <FormField
                name={'blockLayout'}
                control={control}
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('blockSchedule')}</FormLabel>
                    <BlockLayoutInput
                      {...field}
                      cycleLabel={cycleLabel}
                      sessionLabel={sessionLabel}
                      disableAddCycleButton={isCopy}
                      disabled={isBowlingEdit}
                      isBowlingBlock={blockType === 'bowling'}
                    />
                    {errors.blockLayout && (
                      <FormMessage>
                        {Array.isArray(errors.blockLayout)
                          ? errors.blockLayout
                              .map((err) => err?.message)
                              .filter(Boolean)
                              .join(', ')
                          : errors.blockLayout?.message}
                      </FormMessage>
                    )}
                  </FormItem>
                )}
              />
            ) : (
              <div className="flex gap-2">
                <FormField
                  name={'cycleCount'}
                  control={control}
                  render={({ field }) => (
                    <FormItem>
                      <TextField
                        label={
                          cycleLabel
                            ? t('trainingCycles', {
                                cycles: capitalize(cycleLabel),
                              })
                            : t('trainingCyclesDefault')
                        }
                        {...field}
                        disabled={isEdit || isCopy}
                        error={Boolean(errors.cycleCount)}
                        helperText={
                          cycleLabel
                            ? errors.cycleCount?.message?.replace(
                                'Cycle',
                                cycleLabel,
                              )
                            : errors.cycleCount?.message
                        }
                        onChange={(e) => {
                          const newCycleCount = parseInt(e.target.value) || 0;
                          field.onChange(newCycleCount);
                          const currentSessionCount =
                            form.getValues('sessionCount');
                          form.setValue(
                            'blockLayout',
                            getBlockScheduleFromCount(
                              newCycleCount,
                              currentSessionCount,
                            ),
                          );
                        }}
                      />
                    </FormItem>
                  )}
                />
                <FormField
                  name={'sessionCount'}
                  control={control}
                  render={({ field }) => (
                    <FormItem>
                      <TextField
                        label={
                          sessionLabel
                            ? t('trainingSessions', {
                                sessions: capitalize(sessionLabel),
                              })
                            : t('trainingSessionsDefault')
                        }
                        {...field}
                        disabled={isEdit || isCopy || blockType === 'bowling'}
                        error={Boolean(errors.sessionCount)}
                        helperText={
                          sessionLabel
                            ? errors.sessionCount?.message?.replace(
                                'Session',
                                sessionLabel,
                              )
                            : errors.sessionCount?.message
                        }
                        onChange={(e) => {
                          const newSessionCount = parseInt(e.target.value) || 0;
                          field.onChange(newSessionCount);
                          const currentCycleCount =
                            form.getValues('cycleCount');
                          form.setValue(
                            'blockLayout',
                            getBlockScheduleFromCount(
                              currentCycleCount,
                              newSessionCount,
                            ),
                          );
                        }}
                      />
                    </FormItem>
                  )}
                />
              </div>
            )}

            {blockType !== 'bowling' && (
              <>
                <FormField
                  name={'focuses'}
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <FormItem>
                      <MultiSelect
                        label={t('focus')}
                        selectedOptions={focusOptions.filter((f) =>
                          value.map((v) => v.id).includes(f.id),
                        )}
                        options={focusOptions}
                        onChange={(e) => {
                          onChange(e);
                        }}
                        optionToName={(f) => f.name}
                      />
                    </FormItem>
                  )}
                />

                <FormField
                  name={'trainingMaxRatio'}
                  control={control}
                  render={({ field }) => (
                    <FormItem>
                      <TextField
                        label={t('trainingMaxRatio')}
                        {...field}
                        fullWidth
                        error={Boolean(errors.trainingMaxRatio)}
                        helperText={errors.trainingMaxRatio?.message}
                      />
                    </FormItem>
                  )}
                />

                <div className="flex flex-col gap-1">
                  <FormField
                    name={'autoSchedule'}
                    control={control}
                    render={({ field }) => (
                      <FormItem className="flex items-center gap-2">
                        <Checkbox
                          {...field}
                          checked={!!field.value}
                          inputProps={{
                            'aria-label': t('autoScheduleBlockLabel'),
                          }}
                        />
                        <Typography variant="body2">
                          {t('autoScheduleBlockLabel')}
                        </Typography>
                        <InfoTooltip
                          description={t('autoScheduleBlockTooltip')}
                          icon={<MdInfo size={16} />}
                        />
                      </FormItem>
                    )}
                  />
                  <FormField
                    name={'allowFlexWorkouts'}
                    control={control}
                    render={({ field }) => {
                      const isFlexLocked =
                        isEdit && defaultValues.allowFlexWorkouts === true;

                      return (
                        <FormItem className="flex items-center gap-2">
                          <Checkbox
                            {...field}
                            checked={!!field.value}
                            disabled={isFlexLocked}
                            inputProps={{
                              'aria-label': t('allowAnytimeWorkouts'),
                            }}
                          />
                          <Typography variant="body2">
                            {t('allowAnytimeWorkouts')}
                          </Typography>
                          <InfoTooltip
                            title={t('allowAnytimeWorkouts')}
                            description={t(
                              'allowAnytimeWorkoutsTooltipDescription',
                            )}
                            icon={<MdInfo size={16} />}
                          />
                        </FormItem>
                      );
                    }}
                  />
                </div>
              </>
            )}
          </div>
        </form>
      </Form>
      <DrawerFooter
        className={cn(
          isEdit && !isCopy && !!onDelete ? 'justify-between' : 'justify-end',
          'flex flex-row gap-2 w-full p-8',
        )}
      >
        {isEdit && !isCopy && !!onDelete && (
          <>
            <Button
              variant="containedDestructive"
              onClick={() => setDeleteModalOpen(true)}
              disabled={hasBeenPublished}
            >
              {t('delete')}
            </Button>
            <div className="flex-grow" />
          </>
        )}
        <Button variant="outlined" onClick={closeHandler}>
          {t('cancel')}
        </Button>
        <Button
          type="submit"
          form="block-form"
          disabled={!form.formState.isValid}
        >
          {t('save')}
        </Button>
      </DrawerFooter>
      <DeleteConfirmationModal
        id={defaultValues?.id ?? ''}
        open={deleteModalOpen}
        {...deleteBlockText(t)}
        onClose={() => setDeleteModalOpen(false)}
        onDelete={() => onDelete?.()}
      />
      <DeleteConfirmationModal
        id={(defaultValues?.id ?? '') + '-weeks'}
        open={deleteWeeksModalOpen}
        {...deleteBlockWorkoutsText(t)}
        onClose={() => setDeleteWeeksModalOpen(false)}
        onDelete={() => {
          setDeleteWeeksModalOpen(false);
          onSave(form.getValues());
        }}
        confirmText={t('continue')}
      />
      <ConfirmationModal
        id={defaultValues?.id ?? ''}
        open={cancelModalOpen}
        title={t('cancelBlockActionTitle', { action: actionTerm })}
        message={
          isEdit
            ? t('cancelEditingBlockMessage')
            : isCopy
              ? t('cancelCopyingBlockMessage')
              : t('cancelCreatingBlockMessage')
        }
        onConfirm={() => {
          setCancelModalOpen(false);
          onClose();
        }}
        onCancel={() => setCancelModalOpen(false)}
      />
    </Drawer>
  );
}

const getBlockTypeOptionLabel = (
  type: BlockType,
  t: I18nTranslator,
): string => {
  const translations: Record<BlockType, string> = {
    strength_training: t('strengthTraining'),
    bowling: t('bowling'),
  };
  return translations[type];
};

const getBlockTypeOptions = (role: ProfileRoleType | undefined) => {
  const blockTypeFilter: BlockType[] =
    role === CRICKET_BOWLING_COACH_PROFILE_ROLE ? ['bowling'] : [];
  return blockTypeFilter.length > 0
    ? zBlockType.options.filter((type) =>
        blockTypeFilter.includes(type as BlockType),
      )
    : zBlockType.options;
};
